import React from 'react';
import { 
    Chip, 
    Tooltip, 
    Typography, 
    CircularProgress,
    IconButton,
    Popover,
    makeStyles 
} from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import UnavailableReason, { DIMMED } from './UnavailableReason';

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        alignItems: 'center',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    // Floating indicator wrapper - positioned absolutely so it doesn't affect layout
    floatingIndicatorWrapper: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: theme.spacing(1),
        height: 28,
        width: 28,
        zIndex: 10
    },
    // Compact chip - icon only by default, expands LEFT on hover
    compactChip: {
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        transformOrigin: 'right center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        cursor: 'default',
        // Icon-only state
        minWidth: 28,
        width: 28,
        padding: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        '& .MuiChip-label': {
            opacity: 0,
            width: 0,
            padding: 0,
            color: '#ffffff',
            transition: 'opacity 0.2s ease-in-out, width 0.2s ease-in-out, padding 0.2s ease-in-out'
        },
        '& .MuiChip-icon': {
            margin: 0,
            color: '#ffffff'
        },
        // Hover state - expand LEFT to show label
        '&:hover': {
            width: 'auto',
            minWidth: 'auto',
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
            '& .MuiChip-label': {
                opacity: 1,
                width: 'auto',
                paddingLeft: theme.spacing(0.5),
                paddingRight: theme.spacing(0.5),
                color: '#ffffff'
            },
            '& .MuiChip-icon': {
                marginRight: theme.spacing(0.5),
                color: '#ffffff'
            }
        }
    },
    chip: {
        marginLeft: theme.spacing(1)
    },
    warningChip: {
        backgroundColor: theme.palette.warning.dark,
        color: '#ffffff',
        '& .MuiChip-icon': {
            color: '#ffffff'
        }
    },
    errorChip: {
        backgroundColor: theme.palette.error.dark,
        color: '#ffffff',
        '& .MuiChip-icon': {
            color: '#ffffff'
        }
    },
    successChip: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff',
        '& .MuiChip-icon': {
            color: '#ffffff'
        }
    },
    // Loading spinner - compact
    loadingSpinner: {
        marginLeft: theme.spacing(1)
    },
    loadingContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
    },
    tooltipContent: {
        maxWidth: 300
    },
    missingList: {
        margin: 0,
        paddingLeft: theme.spacing(2),
        fontSize: '0.85rem'
    },
    menuItemDisabled: {
        opacity: 0.5,
        cursor: 'not-allowed',
        '& .MuiTypography-root': {
            color: theme.palette.text.disabled
        }
    },
    menuItemWarning: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%'
    },
    unavailableIndicator: {
        marginLeft: 'auto',
        fontSize: '0.75rem',
        color: theme.palette.warning.dark,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5)
    },
    hintPre: {
        margin: 0,
        padding: theme.spacing(1),
        fontSize: '0.75rem',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
        overflowX: 'auto'
    }
}));

/**
 * ZoneAvailabilityIndicator
 * 
 * Shows a compact floating indicator next to the zone dropdown.
 * Displays only an icon by default, expands on hover to show status text.
 * Uses absolute positioning so it floats over adjacent form fields without pushing them.
 */
export const ZoneAvailabilityIndicator = ({ 
    isLoading, 
    isAvailable, 
    missingInstances = [],
    error,
    unableToVerify,
    // Optional: shown when no zone is selected yet and the region-wide picture is known.
    regionSummary = null
}) => {
    const classes = useStyles();

    if (isLoading) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <CircularProgress size={18} className={classes.loadingSpinner} />
            </span>
        );
    }

    // Unable to verify state (e.g., auth failures, API errors)
    if (unableToVerify) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title="Unable to verify instance availability. This may be due to authentication issues or API errors.">
                    <Chip
                        icon={<WarningIcon style={{ fontSize: 16 }} />}
                        label="Unable to verify"
                        size="small"
                        className={classes.compactChip + ' ' + classes.warningChip}
                    />
                </Tooltip>
            </span>
        );
    }

    if (error) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title={'Error checking availability: ' + error}>
                    <Chip
                        icon={<ErrorIcon style={{ fontSize: 16 }} />}
                        label="Check failed"
                        size="small"
                        className={classes.compactChip + ' ' + classes.warningChip}
                    />
                </Tooltip>
            </span>
        );
    }

    if (isAvailable === false) {
        var tooltipContent = (
            <div className={classes.tooltipContent}>
                <Typography variant="body2" gutterBottom>
                    The following instances are not available in this zone:
                </Typography>
                <ul className={classes.missingList}>
                    {missingInstances.map(function(inst, idx) {
                        return <li key={idx}>{inst}</li>;
                    })}
                </ul>
            </div>
        );

        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title={tooltipContent}>
                    <Chip
                        icon={<WarningIcon style={{ fontSize: 16 }} />}
                        label={missingInstances.length + ' unavailable'}
                        size="small"
                        className={classes.compactChip + ' ' + classes.errorChip}
                    />
                </Tooltip>
            </span>
        );
    }

    if (isAvailable === null && regionSummary) {
        const { offeredZoneCount, totalZoneCount, missingInstances: regionMissing = [] } = regionSummary;
        if (offeredZoneCount === 0) {
            return (
                <span className={classes.floatingIndicatorWrapper}>
                    <Tooltip title={(
                        <div className={classes.tooltipContent}>
                            <Typography variant="body2" gutterBottom>Not offered in any zone of this region:</Typography>
                            <ul className={classes.missingList}>{regionMissing.map((inst, idx) => <li key={idx}>{inst}</li>)}</ul>
                        </div>
                    )}>
                        <Chip icon={<ErrorIcon style={{ fontSize: 16 }} />} label="Not offered in this region" size="small"
                            className={classes.compactChip + ' ' + classes.errorChip} />
                    </Tooltip>
                </span>
            );
        }
        const all = offeredZoneCount === totalZoneCount;
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title={all ? 'Every zone in this region offers the selected instances' : 'Pick a zone that offers all selected instances; the others are greyed out'}>
                    <Chip icon={all ? <CheckCircleIcon style={{ fontSize: 16 }} /> : <WarningIcon style={{ fontSize: 16 }} />}
                        label={all ? 'Offered in all zones' : `Offered in ${offeredZoneCount} of ${totalZoneCount} zones`} size="small"
                        className={classes.compactChip + ' ' + (all ? classes.successChip : classes.warningChip)} />
                </Tooltip>
            </span>
        );
    }

    // Unknown (null) renders nothing: a green chip for "no data" would be a lie.
    if (isAvailable === true) {
        return (
            <span className={classes.floatingIndicatorWrapper}>
                <Tooltip title="All selected instances are available in this zone">
                    <Chip
                        icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
                        label="All instances available"
                        size="small"
                        className={classes.compactChip + ' ' + classes.successChip}
                    />
                </Tooltip>
            </span>
        );
    }

    return null;
};

/**
 * ZoneMenuItemContent
 * 
 * Renders zone name with availability warning if applicable
 * Use this inside MenuItem components
 */
export const ZoneMenuItemContent = ({
    zoneName,
    zoneId,
    isAvailable,
    missingInstances = [],
    showDetails = false,
    disabled = false,
    reason = null
}) => {
    const classes = useStyles();

    // A row the caller has disabled outright (the zone does not offer a
    // selected shape). The zone name fades; the reason stays readable.
    if (disabled) {
        return (
            <div className={classes.menuItemWarning}>
                <span style={DIMMED}>{zoneName}</span>
                <span style={{ marginLeft: 'auto', paddingLeft: 16 }}><UnavailableReason reason={reason} /></span>
            </div>
        );
    }

    if (isAvailable !== false) {
        return <span>{zoneName}</span>;
    }

    const tooltipContent = showDetails ? (
        <div className={classes.tooltipContent}>
            <Typography variant="body2" gutterBottom>
                Not all selected instances are available:
            </Typography>
            <ul className={classes.missingList}>
                {missingInstances.map((inst, idx) => (
                    <li key={idx}>{inst}</li>
                ))}
            </ul>
        </div>
    ) : `${missingInstances.length} selected instance(s) not available in this zone`;

    return (
        <Tooltip title={tooltipContent} placement="right">
            <div className={classes.menuItemWarning}>
                <span>{zoneName}</span>
                <span className={classes.unavailableIndicator}>
                    <WarningIcon fontSize="small" />
                    <span>Limited</span>
                </span>
            </div>
        </Tooltip>
    );
};

/**
 * InstanceAvailabilityBanner
 * 
 * A banner to show at the top of the form when there are availability issues
 */
export const InstanceAvailabilityBanner = ({
    unavailableZones = [],
    selectedZone,
    isZoneAvailable,
    getMissingInstances,
    isLoading
}) => {
    const classes = useStyles();

    if (isLoading) {
        return null;
    }

    // Only show banner if selected zone has issues
    if (!selectedZone || isZoneAvailable(selectedZone)) {
        return null;
    }

    const missingInstances = getMissingInstances(selectedZone);

    return (
        <div style={{
            padding: '12px 16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: 4,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12
        }}>
            <WarningIcon style={{ color: '#856404', marginTop: 2 }} />
            <div>
                <Typography variant="body1" style={{ color: '#856404', fontWeight: 500 }}>
                    Instance Availability Warning
                </Typography>
                <Typography variant="body2" style={{ color: '#856404' }}>
                    The following instances are not available in zone {selectedZone}:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: 20, color: '#856404' }}>
                    {missingInstances.map((inst, idx) => (
                        <li key={idx}><strong>{inst}</strong></li>
                    ))}
                </ul>
                <Typography variant="body2" style={{ color: '#856404' }}>
                    Please select a different zone or change your instance selections.
                </Typography>
            </div>
        </div>
    );
};

/**
 * Helper function to get zone disabled state for dropdown
 */
export const getZoneDisabledState = (zoneId, isZoneAvailable, allowOverride = false) => {
    // If allowOverride is true, don't disable but show warning
    if (allowOverride) {
        return false;
    }
    return !isZoneAvailable(zoneId);
};

/**
 * Helper function to format zone label with availability info
 */
export const formatZoneLabel = (zone, isZoneAvailable, getMissingInstances) => {
    const isAvailable = isZoneAvailable(zone.zone_id);
    const name = zone.name || zone.zone_id;
    
    if (isAvailable) {
        return name;
    }
    
    const missing = getMissingInstances(zone.zone_id);
    if (missing.length > 0) {
        return `${name} (${missing.length} instance(s) unavailable)`;
    }
    
    return `${name} (Some instances unavailable)`;
};

const PRICING_POLICY_JSON = JSON.stringify({
    Version: '2012-10-17',
    Statement: [{ Effect: 'Allow', Action: ['pricing:GetProducts', 'aws-marketplace:SearchAgreements', 'aws-marketplace:GetAgreementTerms'], Resource: '*' }]
}, null, 2);

/**
 * PricingPermissionHint
 *
 * (i) shown next to the instance picker when AWS refused the price lookup.
 * We cannot grant the permission for the user, so we tell them exactly what
 * to attach to the credential's IAM user.
 */
export const PricingPermissionHint = ({ status, message }) => {
    const classes = useStyles();
    const [anchorEl, setAnchorEl] = React.useState(null);
    if (!status || status === 'ok') return null;

    const denied = status === 'denied';
    return (
        <React.Fragment>
            <Tooltip title={denied ? 'Prices unavailable: click for how to enable them' : 'Prices unavailable: click for details'}>
                <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Pricing unavailable">
                    <InfoOutlinedIcon fontSize="small" color={denied ? 'action' : 'disabled'} />
                </IconButton>
            </Tooltip>
            <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
                <div style={{ padding: 16, maxWidth: 420 }}>
                    <Typography variant="subtitle2" gutterBottom>Prices are not shown</Typography>
                    {denied ? (
                        <React.Fragment>
                            <Typography variant="body2" gutterBottom>
                                This credential cannot read AWS pricing (instance prices need <strong>pricing:GetProducts</strong>;
                                Marketplace software fees need <strong>aws-marketplace:SearchAgreements</strong> and
                                <strong> GetAgreementTerms</strong>). Attach the AWS managed policy
                                <strong> AWSPriceListServiceFullAccess</strong> to its IAM user, or add this statement:
                            </Typography>
                            <pre className={classes.hintPre}>{PRICING_POLICY_JSON}</pre>
                            <Typography variant="caption" color="textSecondary">
                                Prices are read-only and never affect what gets deployed.
                            </Typography>
                        </React.Fragment>
                    ) : (
                        <Typography variant="body2">{message || 'The AWS pricing lookup failed. Everything else still works.'}</Typography>
                    )}
                </div>
            </Popover>
        </React.Fragment>
    );
};

export default {
    ZoneAvailabilityIndicator,
    PricingPermissionHint,
    ZoneMenuItemContent,
    InstanceAvailabilityBanner,
    getZoneDisabledState,
    formatZoneLabel
};
